import React, { ChangeEvent, useEffect, useState } from "react";
import { Alert, Button, DialogActions, DialogContent, DialogTitle, List, ListItemButton, ListItemText, TextField, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

import ResponsiveDialog from "components/dialogs/ResponsiveDialog";
import { useDebounce } from "hooks/debounce";
import APIRequest from "system/APIRequest";
import { InfegyUser } from "system/InfegyUser";

interface SelectUserDialogProps {
  onChange: (user: InfegyUser) => void;
  onClose: () => void;
  open: boolean;
}

interface UserQueryParams {
  admin: number;
  minimal?: number;
  page?: number;
  per_page?: number;
  q: string;
  search?: string;
}

export default function SelectUserDialog(props: SelectUserDialogProps): React.JSX.Element {
  const [search, setSearch] = useState<string>("");
  const [selectedUser, setSelectedUser] = useState<InfegyUser | null>(null);
  const [users, setUsers] = useState<Array<InfegyUser>>([]);
  const {i18n, t} = useTranslation();

  const getUsers = useDebounce(async () => {
    const queryParams: UserQueryParams = {
      admin: 1,
      minimal: 1,
      q: JSON.stringify([{id: "name", op: "not-contain", value: "PENDING INVITE"}])
    }
    if (search) {
      queryParams.search = search;
    }
    try {
      const response = await APIRequest("obj/user", queryParams);
      setUsers(response.output);
    } catch (e) {
      console.warn(e)
    }
  }, 300);

  useEffect(() => {
    if (props.open) {
      getUsers();
    }
  }, [search, props.open])

  return (
    <ResponsiveDialog maxWidth="xs" open={props.open}>
      <DialogTitle>
        <Typography variant="h3">{t("select-user-dialog.title")}</Typography>
      </DialogTitle>
      <DialogContent sx={{maxHeight: 400, paddingBottom: 0}}>
        <TextField autoFocus fullWidth autoComplete="off" onChange={(event: ChangeEvent<HTMLInputElement>) => setSearch(event.target.value)} placeholder={t("select-user-dialog.search-placeholder")}></TextField>
      </DialogContent>
      <DialogContent sx={{maxHeight: 400, paddingTop: 0, paddingBottom: 0}}>
        <List sx={{paddingRight: 0, paddingLeft: 0}}>
          {!!users.length && users.map((user, index: number) => {
            return (
              <ListItemButton key={user.id} onClick={() => setSelectedUser(user)} selected={user.id === selectedUser?.id}>
                <ListItemText>{user.name} ({user.email})</ListItemText>
              </ListItemButton>
            )
          })}
          {search && !users.length &&
            <Alert severity="info">{t("select-user-dialog.found-no-users")}</Alert>
          }
        </List>
      </DialogContent>
      <DialogActions>
        <Button color="secondary" onClick={() => props.onClose()} variant="contained">{t("close")}</Button>
        <Button color="primary" disabled={!selectedUser} onClick={() => selectedUser && props.onChange(selectedUser)} variant="contained">{t("submit")}</Button>
      </DialogActions>
    </ResponsiveDialog>
  )
}